// No import given  from core
// eslint-disable-next-line no-restricted-imports
import React, { useEffect, useState, useRef } from 'react';
import { BaseCSSProperties, CSSProperties, makeStyles } from '@quillbot/design-system';
import { LibErrorBoundary } from '@Common/components/errorBoundary/LibErrorBoundary';
import { useRouter } from 'next/router';
import Head from 'next/head';
import SEO from 'config/SEO';
import { SEOValues } from 'config/models/SEO';
import TrackClass from 'core/logger/track';
import dynamic from 'next/dynamic';
import { RootState, IQuillDrawer as IQuillDrawer } from 'src/_common/models/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Link, Theme, useTheme } from '@quillbot/design-system';
import Sidebar from '@Common/components/Sidebar';
import { switchTheme } from '@Common/handlers/services';
import { setSidebarPinnned, toggleDrawerAction, userClickedThemeBtn } from '@Common/actions/commonActions';
import Footer from '@Common/components/footer/Footer';
import SurveyBanner from '../components/systemNotifications/SurveyBanner';
import Header from '../components/header/Header';
import MinHeader from '../components/header/MinHeader';
import { APP_LANGUAGE } from '@Common/constants';
import { translateText } from '@Common/handlers/helper';
import { Outlet } from 'react-router-dom';
import { ABTestWithFallbackCtrl } from './ABTestWithFallbackCtrl';
import { imgSrc } from '@Common/components/img';

const MaintenanceBar = dynamic(() => import('../components/systemNotifications/MaintenanceBar'), {
  ssr: false,
});
const SessionDialog = dynamic(() => import('../components/systemNotifications/SessionDialog'), {
  ssr: false,
});
// const QuillDrawer = dynamic(() => import('../components/drawer/QuillDrawer'));
const Toast = dynamic(() => import('src/_common/components/systemNotifications/Toast'));
const AlertDialog = dynamic(() => import('../components/systemNotifications/AlertDialog'));
const RecaptchaDialog = dynamic(() => import('../components/systemNotifications/RecaptchaDialog'));
const SaleDialog = dynamic(() => import('@Common/components/SaleDialog'));
const PromotionDialog = dynamic(() => import('@Common/components/PromotionDialog'));
interface IProps {
  children?: React.ElementType;
  guards?: {
    canVisit: boolean[];
    canLoad: boolean[];
  };
}

interface Languages {
  [language: string]: {
    imagePath: string;
    displayText: string;
  };
}

const LANGUAGES: Languages = {
  us: {
    imagePath: '/images/American.svg',
    displayText: 'American English',
  },
  ca: {
    imagePath: '/images/Canada.svg',
    displayText: 'Canadian English',
  },
  uk: {
    imagePath: '/images/British.svg',
    displayText: 'British English',
  },
  au: {
    imagePath: '/images/Australian.svg',
    displayText: 'Australian English',
  },
};

interface StyleProps {
  root: BaseCSSProperties;
  contentShift: CSSProperties;
  isCoWriter: boolean;
  isBannerShown: boolean;
  isMobile: boolean;
}

// type PropsClasses = Record<StyleProps, any>;

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  contentWrapper: {
    marginLeft: 50,
    width: 'calc(100vw - 50px)',
    position: 'relative',
    transition: 'margin-left 0.2s ease-in-out',
    [theme.breakpoints.up(1600)]: {
      marginLeft: 58,
      width: 'calc(100vw - 58px)',
    },
    [theme.breakpoints.down(599)]: {
      marginLeft: 0,
      width: '100%',
    },
  },
  wrapperShift: {
    [theme.breakpoints.up(1080)]: {
      marginLeft: ({ isCoWriter }) => (isCoWriter ? 50 : 210),
    },
    [theme.breakpoints.up(1600)]: {
      marginLeft: ({ isCoWriter }) => (isCoWriter ? 58 : 230),
    },
    [theme.breakpoints.down(1080)]: {
      marginLeft: 50,
    },
    [theme.breakpoints.down(599)]: {
      marginLeft: 0,
    },
  },
  wrapperHoverShift: {
    [theme.breakpoints.up(1080)]: {
      transitionDelay: 500,
    },
  },
  content: {
    transition: 'margin 0.2s ease-in-out',
    marginRight: 0,
  },
  contentShift: {
    transition: 'margin 0.2s ease-in-out',
  },
  BG: {
    // backgroundColor: 'white',
    backgroundColor: theme.palette.grey[50],
  },
  homeBG: {
    // backgroundColor: '#f1f1f1',
  },
  headerWrapper: {
    zIndex: 1202,
  },
  quillHeaderWraqpper: {
    position: 'sticky',
    width: '100%',
    top: 0,
    zIndex: 1202,
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
    },
  },
  termsIndependentFooter: {
    fontSize: 10,
    padding: '4px 12px',
    textAlign: 'center',
    backgroundColor: theme.palette.background.default,
  },
}));

const MainComponent = ({ props }: { props: IProps }) => {
  const classes = useStyles({} as StyleProps);
  const independentTool = useSelector((store: RootState) => store.common.independentTool);

  const openedDrawer: IQuillDrawer = useSelector((state: RootState) => state?.common?.quillDrawer as IQuillDrawer);
  const isOpencompareModeDrawer = useSelector((store: RootState) => store.common.isOpencompareModeDrawer);

  const appLanguage = useSelector((rootState: RootState) => rootState.common.appLanguage);

  const router = useRouter();

  const getMarginRight = () => {
    if (isOpencompareModeDrawer && router.pathname === '/') return 340;
    if (process.browser && openedDrawer && window.innerWidth >= 1350) {
      return Math.min(window.innerWidth * 0.25, 350);
    }
    return 0;
  };

  return (
    <Grid item className={classes.BG} style={{ minHeight: independentTool ? '100vh' : 'calc(100vh - 280px)' }} xs={12}>
      <Grid container spacing={0}>
        <Grid
          style={{ marginRight: getMarginRight() }}
          className={`${classes.content} ${classes.contentShift}`}
          item
          xs={12}>
          <LibErrorBoundary>
            <Outlet />
          </LibErrorBoundary>
        </Grid>
      </Grid>

      {independentTool && (
        <Box className={classes.termsIndependentFooter}>
          QuillBot{' '}
          <Link>
            <a
              href={`/privacy${appLanguage === APP_LANGUAGE.German ? '?language=DE' : ''}`}
              rel="noopener noreferrer"
              target="_blank">
              {translateText('Privacy Policy')}
            </a>
          </Link>{' '}
          {translateText('and')}{' '}
          <Link>
            <a
              href={`/terms${appLanguage === APP_LANGUAGE.German ? '?language=DE' : ''}`}
              rel="noopener noreferrer"
              target="_blank">
              {translateText('Terms of Service')}
            </a>
          </Link>{' '}
          {translateText('apply.')}
        </Box>
      )}
    </Grid>
  );
};

export function QuillLayout(props: IProps) {
  const [sidebarHoverOpen, setSidebarHoverOpen] = useState(false);
  const [isBannerShown, setIsBannerShown] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerWrapper = useRef<HTMLDivElement>(null);
  const native = useSelector((rootState: RootState) => rootState.common.platform.native);
  const isExtension = useSelector((rootState: RootState) => rootState.common.platform.isExtension);
  const toggledThemeBtn = useSelector((state: RootState) => state.common.userClickedThemeBtn);

  const sidebarPinned = useSelector((rootState: RootState) => rootState.common.sidebarPinned);
  const quillDrawerStatus = useSelector((rootState: RootState) => rootState.common.quillDrawer);
  const router = useRouter();
  const dispatch = useDispatch();
  const theme = useTheme();
  const pageUrl = router.pathname;
  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const classes = useStyles({
    isCoWriter: pageUrl.includes('/co-writer'),
    isBannerShown,
    isMobile,
  } as StyleProps);

  const independentTool = useSelector((store: RootState) => store.common.independentTool);
  // const inputText = useSelector(
  //   (rootState: RootState) => rootState?.pphr?.inputSentences?.sentenceList
  // );
  // const queryObj = new URLSearchParams(this.location.search);
  /* if (typeof urlParams === "string" && urlParams) {
      referrer = urlParams.match(/referrer=([^&]+)/)[1];
    } */
  /* if (queryObj?.referrer) {
    referrer = queryObj'referrer');
  } else if (this.location.state && typeof this.location.state === 'object') {
    referrer = this.location.state.referrer;
  } */

  const getSeoValueForKey = (valueKey: SEOValues) => {
    if (SEO[pageUrl]) {
      return SEO[pageUrl][valueKey] ? SEO[pageUrl][valueKey] : SEO['/'][valueKey];
    }
    return SEO['/'][valueKey];
  };

  const generateReferrer = (queryObj: any) => (queryObj?.gspk ? 'PartnerStack' : '');

  const onLoad = () => {
    const excludedRoutes = ['/signup', '/login', '/co-writer'];
    if (!excludedRoutes.includes(pageUrl)) {
      const queryObj = router.query;
      const referrer = queryObj?.referrer || generateReferrer(queryObj);
      TrackClass.getInstance().visitedPage(pageUrl, referrer as string);
      // remove referrer once it is sent to track
      if (referrer) {
        delete queryObj.referrer;
        router.replace({ pathname: router.pathname, query: queryObj });
      }
    }
  };

  useEffect(() => {
    if (sidebarPinned) {
      if (isExtension || isMobile || native || router.pathname === '/co-writer') {
        dispatch(setSidebarPinnned(false));
      }
    }
    if (quillDrawerStatus !== null) {
      dispatch(toggleDrawerAction(null));
    }
  }, []);

  useEffect(() => {
    onLoad();
    window.scrollTo(0, 0);

    // return () => window.removeEventListener('load', onLoad);
  }, [pageUrl]);

  useEffect(() => {
    setBannerHeight(bannerWrapper.current?.clientHeight || 0);
  }, [sidebarPinned]);

  const handleThemeClick = () => {
    router.pathname === '/' ? dispatch(userClickedThemeBtn(!toggledThemeBtn)) : switchTheme();
  };

  const headerBlock = (
    <>
      <Header handleThemeChange={handleThemeClick} LANGUAGES={LANGUAGES} isBannerShown={isBannerShown} />
      <Sidebar
        sidebarHoverOpen={sidebarHoverOpen}
        setSidebarHoverOpen={setSidebarHoverOpen}
        handleThemeChange={handleThemeClick}
        LANGUAGES={LANGUAGES}
        isBannerShown={isBannerShown}
        bannerHeight={bannerHeight}
      />
    </>
  );

  const mainComponentBlock = (
    <Grid
      item
      className={`${classes.contentWrapper} ${sidebarPinned && classes.wrapperShift} ${
        sidebarHoverOpen && classes.wrapperHoverShift
      }`}>
      <MainComponent props={props} />
      {!(props as any).isExtensionAtServer &&
        !native &&
        router.pathname.indexOf('co-writer') < 0 &&
        !independentTool && <Footer />}
    </Grid>
  );

  return independentTool ? (
    <MainComponent props={props} />
  ) : (
    <>
      <Head>
        <link rel="canonical" href={getSeoValueForKey('canonical')} />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <meta name="viewport" content="width=device-width,initial-scale=1,user-scalable=no" />
        <meta name="description" content={getSeoValueForKey('description')} />
        <meta name="keywords" content={getSeoValueForKey('keyword')} />
        <meta property="og:url" key="url" content={getSeoValueForKey('og_url')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" key="title" content={getSeoValueForKey('title')} />
        <meta property="og:description" key="description" content={getSeoValueForKey('description')} />
        <meta property="og:image" content={imgSrc('/images/og_quillbot.png')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image:src" content={imgSrc('/images/og_quillbot.png')} />
        <link rel="alternate" hrefLang="x-default" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-us" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-pk" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-ng" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-ph" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-gb" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-bd" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-ca" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-gh" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-au" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-de" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-jp" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-za" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-my" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-nl" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-pl" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-zw" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-se" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-be" href={`https://quillbot.com${pageUrl}`} />
        <link rel="alternate" hrefLang="en-il" href={`https://quillbot.com${pageUrl}`} />
        <link rel="shortcut icon" href="/favicon.png" />
        <link
          rel="chrome-webstore-item"
          href="https://chrome.google.com/webstore/detail/iidnbdjijdkbmajdffnidomddglmieko"
        />
        <title>{getSeoValueForKey('title')}</title>
      </Head>
      <div style={{ display: 'none' }}>
        {/* <h1>{getSeoValueForKey('flash_page_title')}</h1> */}
        {getSeoValueForKey('flash_page_description')}
      </div>
      <Grid container spacing={0}>
        <Grid item ref={bannerWrapper} style={{ width: '100%' }}>
          <MaintenanceBar />
          <SurveyBanner setIsBannerShown={(value: boolean) => setIsBannerShown(value)} />
        </Grid>
        {/* <PlagCheckUpsell /> */}
        <Grid item className={classes.quillHeaderWraqpper}>
          <Grid item xs={12} className={classes.headerWrapper}>
            {headerBlock}
          </Grid>
        </Grid>
        <>{mainComponentBlock}</>
        <Toast />
        <RecaptchaDialog />
        <AlertDialog />
        <SessionDialog />
        <SaleDialog />
        <PromotionDialog />
      </Grid>
    </>
  );
}
